import { useState, useCallback, useContext, useRef, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';

import {
	REFRESH_EDITOR_CONTENT_EXPERIENCE,
	IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	EDIT_PAGE_V2,
	EDIT_PAGE_EMBED,
	VIEW_PAGE,
} from '@confluence/named-routes/entry-points/routes.ts';
import type { Route } from '@confluence/route/entry-points/Route.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { refetchEditorContent } from '@confluence/editor-features/entry-points/refetchEditorContent.ts';
import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import {
	getInEditorTemplatesPanelQueryParamValue,
	isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen,
	removeInEditorTemplatesQueryParam,
	setInEditorTemplatesPanelQueryParam,
	isInEditorTemplatesPanelQueryParamValueOpen,
	IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED,
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
	CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME,
	getCreatedWithTemplateQueryParamValue,
} from '@confluence/template-utils/entry-points/urlUtils.ts';

const POST_BE_SYNCHRONOUS_REFRESH_EDITOR_TIMEOUT = 1500;
const DELAY_INITIAL_CHECK_TIMEOUT = 4000;

const isContentCreationPanelOpen = (getQueryParams): boolean => {
	return isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen(getQueryParams);
};

type Props = {
	cancelDebouncedSaveDraft: () => void;
	contentId: string;
	draftShareId?: string;
	editorActions: EditorActions;
	hideSpinner: () => void;
	showSpinner: () => void;
	isUnpublishedDraft: boolean | null;
	isEmbeddedEditor?: boolean;
};

export const useEditorContextPanel = ({
	cancelDebouncedSaveDraft,
	contentId,
	draftShareId,
	editorActions,
	hideSpinner,
	showSpinner,
	isUnpublishedDraft,
	isEmbeddedEditor,
}: Props) => {
	const { setQueryParams, getQueryParams, replace } = useContext(RoutesContext);
	const _skipLocalChangesCheck = useRef<boolean>(
		getCreatedWithTemplateQueryParamValue(getQueryParams),
	);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [hasUserAddedContent, setHasUserAddedContent] = useState<boolean | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();

	useEffect(() => {
		setTimeout(() => {
			_skipLocalChangesCheck.current = false;
		}, DELAY_INITIAL_CHECK_TIMEOUT);
		/* eslint-disable react-hooks/exhaustive-deps */
	}, []);

	const onDocChange = useCallback(() => {
		const editorHasContent = !editorActions.isDocumentEmpty();
		const wasTemplatesPanelAutoClosed =
			getInEditorTemplatesPanelQueryParamValue(getQueryParams) ===
			IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED;

		if (isUnpublishedDraft && !editorHasContent && wasTemplatesPanelAutoClosed) {
			setInEditorTemplatesPanelQueryParam(setQueryParams, IN_EDITOR_TEMPLATES_QUERY_OPEN);
		}

		if (!_skipLocalChangesCheck.current) {
			if (isInEditorTemplatesPanelQueryParamValueOpen(getQueryParams) && editorHasContent) {
				setQueryParams(
					{
						[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED,
						[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME]: null,
					},
					true,
				);
			}

			setHasUserAddedContent(editorHasContent);
		}
	}, [editorActions, _skipLocalChangesCheck, getQueryParams, setQueryParams, isUnpublishedDraft]);

	useEffect(() => {
		subscribeDocChange(onDocChange);
		return () => unsubscribeDocChange(onDocChange);
	}, [onDocChange]);

	const onClickCloseContentCreationPanel = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'templateDrawer',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'templateDismissButton',
			},
		}).fire();

		removeInEditorTemplatesQueryParam(setQueryParams);
	}, [setQueryParams, createAnalyticsEvent]);

	const onClickOpenContentCreationPanel = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'dropdownMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'openTemplateBrowser',
			},
		}).fire();

		setInEditorTemplatesPanelQueryParam(setQueryParams, IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN);
	}, [setQueryParams, createAnalyticsEvent]);

	const beforeUpdateRequest = useCallback(() => {
		showSpinner();
		cancelDebouncedSaveDraft();
		_skipLocalChangesCheck.current = true;
	}, [showSpinner, cancelDebouncedSaveDraft, _skipLocalChangesCheck]);

	const postUpdateRequest = useCallback(() => {
		setHasUserAddedContent(false);

		setInEditorTemplatesPanelQueryParam(setQueryParams, IN_EDITOR_TEMPLATES_QUERY_OPEN);

		setTimeout(() => {
			_skipLocalChangesCheck.current = false;
			hideSpinner();
		}, POST_BE_SYNCHRONOUS_REFRESH_EDITOR_TIMEOUT);
	}, [hideSpinner, _skipLocalChangesCheck, setQueryParams]);

	const handleUpdatedWithTemplate = useCallback(
		async (payload: any) => {
			try {
				showSpinner();
				experienceTracker.start({
					name: REFRESH_EDITOR_CONTENT_EXPERIENCE,
					timeout: ExperienceTimeout.DEFAULT,
				});
				const { spaceKey, subType } = payload;
				await refetchEditorContent(contentId);

				let route: Route;

				let query = draftShareId ? { draftShareId } : {};

				if (isEmbeddedEditor) {
					route = EDIT_PAGE_EMBED;

					const params = getQueryParams();

					query = {
						...query,
						...params,
					};
				} else if (subType === 'live') {
					route = VIEW_PAGE;
					// Since live page don't have the concept of drafts, draftShareId is not needed
					query = {};
				} else {
					route = EDIT_PAGE_V2;
				}

				replace(
					route.toUrl(
						{
							spaceKey,
							contentId,
							contentType: 'pages',
						},
						{
							query,
						},
					),
				);
				experienceTracker.succeed({
					name: REFRESH_EDITOR_CONTENT_EXPERIENCE,
				});
				experienceTracker.succeed({
					name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
				});
			} catch (error) {
				if (error instanceof Error) {
					experienceTracker.stopOnError({
						name: REFRESH_EDITOR_CONTENT_EXPERIENCE,
						error,
					});
					experienceTracker.stopOnError({
						name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
						error,
					});
				}
			} finally {
				hideSpinner();
			}
		},
		[
			contentId,
			cancelDebouncedSaveDraft,
			draftShareId,
			experienceTracker,
			showSpinner,
			hideSpinner,
			replace,
			isEmbeddedEditor,
			getQueryParams,
		],
	);

	const onUpdatedWithTemplateSubscriptionEvent = useCallback(async (response: any) => {
		cancelDebouncedSaveDraft();

		await handleUpdatedWithTemplate({
			spaceKey: response?.contentUpdatedWithTemplate?.spaceKey,
			subType: response?.contentUpdatedWithTemplate?.subtype,
		});
	}, []);

	return {
		beforeUpdateRequest,
		hasUserAddedContent,
		isContentCreationPanelOpen: isContentCreationPanelOpen(getQueryParams),
		onDocChange,
		onClickOpenContentCreationPanel,
		onClickCloseContentCreationPanel,
		postUpdateRequest,
		onUpdatedWithTemplateSubscriptionEvent,
	};
};
